import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Divider, Grid, Stack, Typography} from "@mui/material";
import {Link as RouterLink, Navigate, useLocation} from "react-router-dom";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import SecurityIcon from "@mui/icons-material/Security";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import {Elements, PaymentElement, useElements, useStripe} from "@stripe/react-stripe-js";
import {httpsCallable} from "firebase/functions";
import firebase from "../../core/firebase";
import {stripePromise} from "../../common/stripe";
import {StripePaymentProcessing} from "../../common/components/stripe-payment-processing";
import {MarketingShell} from "../components/marketing-shell";
import {EXCLUSIVE_FULL_BUNDLE_BONUS_BRL, MODULE_PLANS} from "../data/plans";

type CheckoutDraft = {
  businessName: string;
  modules: string[];
  discountPct: number;
  totalMonthly: number;
  bonusApplied: boolean;
  accountName: string;
  accountEmail: string;
};

type PixInfo = {
  imageUrl: string;
  expiresAt: string | null;
};

const SYMPL_GRADIENT = "linear-gradient(135deg, #0f766e 0%, #0d9488 40%, #14b8a6 100%)";

const elementAppearance = {
  theme: "night" as const,
  variables: {
    colorPrimary: "#14b8a6",
    colorBackground: "#020617",
    colorText: "#f8fafc",
    colorDanger: "#ef4444",
    fontFamily: "Inter, system-ui, sans-serif",
    borderRadius: "8px",
  },
};

// ── Payment Form (inner component, must be inside <Elements>) ─────────────────

type PaymentFormProps = {
  onSuccess: () => void | Promise<void>;
  clientSecret: string;
};

const PaymentForm = ({onSuccess, clientSecret}: PaymentFormProps) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pixInfo, setPixInfo] = useState<PixInfo | null>(null);

  const completeSuccess = async () => {
    try {
      await onSuccess();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Pagamento confirmado, mas não foi possível ativar a assinatura.");
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setLoading(true);
    setError(null);

    const {error: submitErr} = await elements.submit();
    if (submitErr) {
      setError(submitErr.message || "Erro ao processar pagamento.");
      setLoading(false);
      return;
    }

    const isSetupIntent = clientSecret.startsWith("seti_");

    if (isSetupIntent) {
      const {error: confirmErr, setupIntent} = await stripe.confirmSetup({
        elements,
        confirmParams: {return_url: `${window.location.origin}/painel`},
        redirect: "if_required",
      });

      if (confirmErr) {
        setError(confirmErr.message || "Validação do cartão recusada.");
        setLoading(false);
        return;
      }

      if (setupIntent?.status === "succeeded") {
        await completeSuccess();
      }
    } else {
      const {error: confirmErr, paymentIntent} = await stripe.confirmPayment({
        elements,
        confirmParams: {return_url: `${window.location.origin}/painel`},
        redirect: "if_required",
      });

      if (confirmErr) {
        setError(confirmErr.message || "Pagamento recusado.");
        setLoading(false);
        return;
      }

      if (paymentIntent?.status === "requires_action") {
        // PIX: show the QR code from next_action
        const nextAction = (paymentIntent as unknown as {next_action?: {pix_display_qr_code?: {image_url_png?: string; expires_at?: number}}}).next_action;
        if (nextAction?.pix_display_qr_code) {
          const expiresAt = nextAction.pix_display_qr_code.expires_at
            ? new Date(nextAction.pix_display_qr_code.expires_at * 1000).toLocaleTimeString("pt-BR")
            : null;
          setPixInfo({
            imageUrl: nextAction.pix_display_qr_code.image_url_png || "",
            expiresAt,
          });
          setLoading(false);
          return;
        }
      }

      if (paymentIntent?.status === "succeeded") {
        await completeSuccess();
      }
    }

    setLoading(false);
  };



  if (pixInfo) {
    return (
      <Stack spacing={3} alignItems="center">
        <QrCode2Icon sx={{fontSize: 48, color: "#14b8a6"}} />
        <Typography variant="h6" sx={{color: "#f8fafc", fontWeight: 800, textAlign: "center"}}>
          Pague via PIX
        </Typography>
        <Typography sx={{color: "rgba(226,232,240,0.7)", textAlign: "center", fontSize: "0.9rem"}}>
          Escaneie o QR code abaixo no seu banco. Seu ambiente será liberado automaticamente após o pagamento.
        </Typography>
        {pixInfo.imageUrl && (
          <Box
            component="img"
            src={pixInfo.imageUrl}
            alt="QR Code PIX"
            sx={{width: 200, height: 200, borderRadius: 2, bgcolor: "#fff", p: 1}}
          />
        )}
        {pixInfo.expiresAt && (
          <Typography sx={{color: "rgba(226,232,240,0.5)", fontSize: "0.8rem"}}>
            Expira às {pixInfo.expiresAt}
          </Typography>
        )}
        <Alert severity="info" sx={{bgcolor: "rgba(20,184,166,0.08)", border: "1px solid rgba(20,184,166,0.2)", color: "#2dd4bf", "& .MuiAlert-icon": {color: "#2dd4bf"}}}>
          Aguardando confirmação do pagamento...
        </Alert>
        <Button
          component={RouterLink}
          to="/painel"
          variant="contained"
          fullWidth
          sx={{background: SYMPL_GRADIENT, fontWeight: 800, py: 1.5}}
        >
          Já paguei — Acessar meu Painel
        </Button>
      </Stack>
    );
  }

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Stack spacing={3}>
        <PaymentElement
          options={{layout: "accordion"}}
        />
        {error && (
          <Alert severity="error" sx={{bgcolor: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.3)", color: "#fca5a5"}}>
            {error}
          </Alert>
        )}
        <Button
          type="submit"
          variant="contained"
          size="large"
          fullWidth
          disabled={loading || !stripe}
          sx={{background: SYMPL_GRADIENT, fontWeight: 800, py: 1.5, fontSize: "1.05rem", boxShadow: "0 8px 24px rgba(13,148,136,0.4)"}}
        >
          {loading ? <CircularProgress size={22} sx={{color: "#fff"}} /> : "Confirmar Assinatura"}
        </Button>
        <Button
          component={RouterLink}
          to="/planos"
          variant="text"
          sx={{color: "rgba(226,232,240,0.5)", fontWeight: 600, "&:hover": {color: "#f8fafc", bgcolor: "transparent"}}}
        >
          Voltar aos planos
        </Button>
      </Stack>
    </Box>
  );
};

// ── Main Checkout Page ────────────────────────────────────────────────────────

export const MarketingCheckoutPage = () => {
  const location = useLocation();
  const draft = location.state as CheckoutDraft | null;
  const initStartedRef = useRef(false);

  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [requiresPayment, setRequiresPayment] = useState(true);
  const [success, setSuccess] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [initError, setInitError] = useState<string | null>(null);
  const [loadingInit, setLoadingInit] = useState(true);

  const selectedModuleNames = useMemo(
    () => draft?.modules.map((id) => MODULE_PLANS.find((m) => m.id === id)?.name || id) || [],
    [draft]
  );
  const visibleModules = selectedModuleNames.slice(0, 8);
  const hiddenModules = Math.max(0, selectedModuleNames.length - visibleModules.length);

  useEffect(() => {
    if (!draft) return;
    if (initStartedRef.current) return;
    initStartedRef.current = true;

    const init = async () => {
      try {
        const fn = httpsCallable<{moduleIds: string[]}, {code: number; data?: {requiresPayment: boolean; clientSecret?: string | null; subscriptionId?: string}}>(
          firebase.functions,
          "stripeCreateSubscriptionCheckout"
        );
        const res = await fn({moduleIds: draft.modules});
        const data = res.data;
        if (data.code !== 200 || !data.data) {
          setInitError("Não foi possível iniciar o pagamento. Tente novamente.");
          return;
        }
        if (!data.data.requiresPayment) {
          // Free plan: modules already activated.
          setRequiresPayment(false);
          setSuccess(true);
          return;
        }
        setClientSecret(data.data.clientSecret || null);
      } catch {
        setInitError("Erro ao conectar com o servidor de pagamento.");
      } finally {
        setLoadingInit(false);
      }
    };
    init();
  }, [draft]);

  const handleStripeConfirmed = useCallback(() => {
    setProcessingPayment(true);
  }, []);

  const handleSubscriptionConfirmed = useCallback(() => {
    setProcessingPayment(false);
    setSuccess(true);
  }, []);

  if (!draft) return <Navigate to="/planos" replace />;

  return (
    <MarketingShell>
      <Box sx={{maxWidth: 1000, mx: "auto", px: {xs: 2, md: 0}, pb: 5}}>
        <Grid container spacing={4}>
          {/* Left: Order Summary */}
          <Grid item xs={12} md={6}>
            <Card sx={{height: "100%", bgcolor: "rgba(15,23,42,0.8)", border: "1px solid rgba(20,184,166,0.3)", boxShadow: "0 24px 64px rgba(0,0,0,0.4)", borderRadius: 4, overflow: "hidden"}}>
              <Box sx={{background: SYMPL_GRADIENT, py: 2.5, px: 3, borderBottom: "1px solid rgba(255,255,255,0.1)"}}>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Box sx={{width: 44, height: 44, borderRadius: 2, bgcolor: "rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center"}}>
                    <AutoAwesomeIcon sx={{fontSize: 24, color: "#fff"}} />
                  </Box>
                  <Box>
                    <Typography variant="overline" sx={{opacity: 0.95, fontWeight: 800, letterSpacing: "0.15em", lineHeight: 1.2, color: "#fff"}}>
                      Symplou Premium
                    </Typography>
                    <Typography variant="h6" fontWeight={900} sx={{letterSpacing: "-0.02em", lineHeight: 1.2, color: "#fff"}}>
                      Resumo do Pedido
                    </Typography>
                  </Box>
                </Stack>
              </Box>

              <CardContent sx={{p: {xs: 3, md: 4}}}>
                <Stack spacing={3}>
                  <Box sx={{p: 2.5, borderRadius: 3, bgcolor: "rgba(2,6,23,0.5)", border: "1px solid rgba(148,163,184,0.15)"}}>
                    <Typography sx={{color: "rgba(226,232,240,0.7)", fontSize: "0.85rem", textTransform: "uppercase", letterSpacing: "0.05em", mb: 0.5, fontWeight: 700}}>
                      Dados do Assinante
                    </Typography>
                    <Typography sx={{fontWeight: 800, color: "#f8fafc", fontSize: "1.1rem"}}>{draft.accountName}</Typography>
                    <Typography sx={{color: "rgba(226,232,240,0.7)", fontSize: "0.95rem"}}>{draft.accountEmail}</Typography>
                  </Box>

                  <Box>
                    <Typography sx={{fontWeight: 800, mb: 1.5, color: "#f8fafc"}}>
                      Módulos da Loja ({draft.businessName}):
                    </Typography>
                    <Stack spacing={1}>
                      {visibleModules.map((name) => (
                        <Stack key={name} direction="row" alignItems="center" spacing={1.5}>
                          <CheckCircleOutlineIcon sx={{color: "#14b8a6", fontSize: 18}} />
                          <Typography sx={{color: "rgba(226,232,240,0.9)", fontSize: "0.95rem"}}>{name}</Typography>
                        </Stack>
                      ))}
                      {hiddenModules > 0 && (
                        <Typography sx={{color: "#2dd4bf", fontSize: "0.85rem", fontWeight: 700, mt: 1}}>
                          + {hiddenModules} módulo(s) avançado(s)
                        </Typography>
                      )}
                    </Stack>
                  </Box>

                  <Divider sx={{borderColor: "rgba(148,163,184,0.1)"}} />

                  <Stack spacing={1.5}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography sx={{color: "rgba(226,232,240,0.7)", fontSize: "1rem"}}>Desconto progressivo</Typography>
                      <Chip label={`- ${draft.discountPct}%`} size="small" sx={{bgcolor: "rgba(20,184,166,0.15)", color: "#2dd4bf", fontWeight: 800, borderRadius: 1}} />
                    </Stack>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-end">
                      <Typography sx={{color: "#f8fafc", fontSize: "1.1rem", fontWeight: 700}}>Total mensal</Typography>
                      <Typography sx={{fontSize: "2rem", fontWeight: 900, color: "#14b8a6", lineHeight: 1}}>
                        R$ {draft.totalMonthly.toFixed(2).replace(".", ",")}
                      </Typography>
                    </Stack>
                  </Stack>

                  {draft.bonusApplied && (
                    <Alert severity="success" sx={{bgcolor: "rgba(13,148,136,0.1)", border: "1px solid rgba(20,184,166,0.3)", color: "#2dd4bf", "& .MuiAlert-icon": {color: "#2dd4bf"}}}>
                      Bônus de <strong>R$ {EXCLUSIVE_FULL_BUNDLE_BONUS_BRL},00</strong> em crédito de IA garantido.
                    </Alert>
                  )}
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {/* Right: Payment */}
          <Grid item xs={12} md={6}>
            <Card sx={{height: "100%", bgcolor: "rgba(15,23,42,0.8)", border: "1px solid rgba(148,163,184,0.15)", boxShadow: "0 12px 32px rgba(0,0,0,0.2)", borderRadius: 4}}>
              <CardContent sx={{p: {xs: 3, md: 4}}}>
                <Stack spacing={3}>
                  {processingPayment ? (
                    <StripePaymentProcessing
                      expectedModuleIds={draft.modules}
                      onConfirmed={handleSubscriptionConfirmed}
                      continueLabel="Ir para o Painel"
                      onContinue={() => {
                        window.location.href = "/painel";
                      }}
                    />
                  ) : success ? (
                    <Stack spacing={2} alignItems="center">
                      <VerifiedUserIcon sx={{fontSize: 64, color: "#14b8a6"}} />
                      <Typography variant="h6" sx={{color: "#f8fafc", fontWeight: 800}}>Tudo Certo!</Typography>
                      <Typography sx={{color: "rgba(226,232,240,0.7)", textAlign: "center"}}>
                        Sua assinatura foi processada e seu ambiente já está liberado.
                      </Typography>
                      <Button
                        component={RouterLink}
                        to="/painel"
                        variant="contained"
                        fullWidth
                        size="large"
                        sx={{background: SYMPL_GRADIENT, fontWeight: 800, py: 1.5, mt: 2}}
                      >
                        Acessar meu Painel
                      </Button>
                    </Stack>
                  ) : loadingInit ? (
                    <Stack alignItems="center" spacing={2} py={4}>
                      <CircularProgress sx={{color: "#14b8a6"}} />
                      <Typography sx={{color: "rgba(226,232,240,0.7)"}}>Preparando pagamento seguro...</Typography>
                    </Stack>
                  ) : initError ? (
                    <Stack spacing={2}>
                      <Alert severity="error">{initError}</Alert>
                      <Button component={RouterLink} to="/planos" variant="outlined">Voltar aos planos</Button>
                    </Stack>
                  ) : (
                    <>
                      <Box>
                        <Stack direction="row" alignItems="center" gap={1} sx={{mb: 1}}>
                          <SecurityIcon sx={{color: "#14b8a6", fontSize: 20}} />
                          <Typography sx={{textTransform: "uppercase", letterSpacing: "0.15em", color: "#14b8a6", fontWeight: 800, fontSize: "0.75rem"}}>
                            Ambiente Seguro
                          </Typography>
                        </Stack>
                        <Typography variant="h5" fontWeight={900} sx={{color: "#f8fafc", mb: 1}}>
                          Finalizar Assinatura
                        </Typography>
                        <Typography sx={{color: "rgba(226,232,240,0.7)"}}>
                          Escolha seu método de pagamento. Seus dados são protegidos pelo Stripe.
                        </Typography>
                      </Box>

                      {!requiresPayment ? (
                        <Stack spacing={2} alignItems="center">
                          <VerifiedUserIcon sx={{fontSize: 64, color: "#14b8a6"}} />
                          <Typography sx={{color: "#f8fafc", fontWeight: 700}}>Plano gratuito ativado!</Typography>
                          <Button component={RouterLink} to="/painel" variant="contained" fullWidth sx={{background: SYMPL_GRADIENT, fontWeight: 800, py: 1.5}}>
                            Acessar meu Painel
                          </Button>
                        </Stack>
                      ) : !stripePromise ? (
                        <Alert severity="warning">
                          Chave pública de pagamento não configurada. Contate o suporte.
                        </Alert>
                      ) : clientSecret ? (
                        <Elements
                          stripe={stripePromise}
                          options={{clientSecret, appearance: elementAppearance}}
                        >
                          <PaymentForm onSuccess={handleStripeConfirmed} clientSecret={clientSecret} />
                        </Elements>
                      ) : (
                        <Alert severity="error">
                          O servidor não retornou a autorização do pagamento. Tente novamente ou contate o suporte.
                        </Alert>
                      )}
                    </>
                  )}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </MarketingShell>
  );
};
